<template>
    <div class="min-h-screen bg-gray-50 dark:bg-echoes-background-black flex items-center justify-center">
        <div class="max-w-md w-full bg-white dark:bg-echoes-background-gray rounded-lg shadow-lg p-2" v-if="!loading">
            <div class="py-8 px-4">
                <div class="w-16 h-16 bg-emerald-300/50 rounded-full flex items-center justify-center mx-auto mb-4" v-if="sale">
                    <svg class="w-8 h-8 text-echoes-background-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>
                <div class="w-16 h-16 bg-red-300/50 rounded-full flex items-center justify-center mx-auto mb-4" v-else>
                    <svg class="w-8 h-8 text-echoes-background-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                </div>
                <div class="text-center">
                    <h1 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">{{ sale ? 'Purchase Successful!' : 'Awaiting confirmation' }}</h1>
                    <p class="text-gray-600 dark:text-gray-400 mb-4">{{ sale ? 'We\'ve confirmed your purchase.' : 'We\'re waiting for the transaction to be confirmed. This may take a few minutes.' }}</p>
                </div>
                <div class="bg-echoes-background-black rounded p-4 mb-4 dark:text-white grid grid-cols-12 gap-2">
                    <div class="col-span-3" v-if="sale?.listing?.preview_image">
                        <img :src="sale?.listing?.preview_image" alt="Dataset" class="h-20 rounded-lg">
                    </div>
                    <div class="col-span-9 text-start" v-if="sale?.listing?.datasets[0]?.name">
                        <div class="">
                            <div class="font-semibold">Dataset: {{ sale?.listing?.datasets[0]?.name }}</div>
                            <div class="text-gray-500">By {{ sale?.listing?.datasets[0]?.seller?.user?.username }}</div>
                            <div class="text-gray-700 dark:text-gray-300" v-if="sale?.final_price">
                                <span class="font-semibold">{{ formatPrice(sale?.final_price) }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-12 grid grid-cols-2 gap-4 mt-2" v-if="sale">
                        <button @click="isEval ? goToEvalResults() : goToPurchases()" class="bg-echoes-primary text-white rounded-lg hover:bg-echoes-primary/80 transition-colors duration-200" v-if="sale?.final_price">
                            {{ isEval ? 'View Eval Results' : 'Download now' }}
                        </button>
                        <button @click="goToExplore" class="bg-echoes-background-gray py-1 rounded-lg border border-echoes-border-light hover:bg-echoes-background-gray/80 transition-colors duration-200">
                            Explore more Datasets
                        </button>
                    </div>
                    <div v-else class="col-span-12 flex justify-center">
                        <button @click="goToExplore" class="bg-echoes-background-gray py-1 px-4 rounded-lg border border-echoes-border-light hover:bg-echoes-background-gray/80 transition-colors duration-200">
                            Return to Datalab
                        </button>
                    </div>
                </div>
                <p class="text-start text-sm text-gray-500 mb-6">
                    Redirecting in {{ countdown }}s...
                </p>
            </div>
        </div>
        <!-- Skeleton -->
        <div class="max-w-md w-full bg-white dark:bg-echoes-background-gray rounded-lg shadow-lg p-2" v-else>
            <div class="py-8 px-4">
                <!-- Skeleton do ícone de sucesso -->
                <div class="w-16 h-16 bg-gray-200 dark:bg-echoes-background-black rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <div class="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded"></div>
                </div>
                
                <!-- Skeleton do título e subtítulo -->
                <div class="text-center mb-4">
                    <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-2 w-64 animate-pulse"></div>
                    <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto w-48 animate-pulse"></div>
                </div>
                
                <!-- Skeleton do card de informações do dataset -->
                <div class="bg-echoes-background-black rounded p-4 mb-4 grid grid-cols-12 gap-2">
                    <!-- Skeleton da imagem -->
                    <div class="col-span-3">
                        <div class="h-20 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                    </div>
                    
                    <!-- Skeleton das informações do dataset -->
                    <div class="col-span-9 text-start">
                        <div class="space-y-2">
                            <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 animate-pulse"></div>
                            <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-pulse"></div>
                            <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3 animate-pulse"></div>
                        </div>
                    </div>
                    
                    <!-- Skeleton dos botões -->
                    <div class="col-span-12 grid grid-cols-2 gap-4 mt-2">
                        <div class="h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                        <div class="h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                    </div>
                </div>
                
                <!-- Skeleton do texto de redirecionamento -->
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/plugins/axios';

const router = useRouter();
const sale = ref(null);
const isEval = ref(false);
const countdown = ref(15);
const loading = ref(false);
let countdownInterval;

const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = Number(price);
    
    if (Number.isInteger(numPrice)) {
        return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' USD';
    }
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD';
};

const goToPurchases = () => {
    router.replace('/creator-profile?tab=purchases');
};

const goToEvalResults = () => {
    router.replace('/creator-profile?page=eval-results&subpage=eval-results');
};

const goToExplore = () => {
    router.replace('/explore');
};

const startCountdown = () => {
    countdownInterval = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
            clearInterval(countdownInterval);
            if(sale.value) {
                if (isEval.value) {
                    goToEvalResults();
                } else {
                    goToPurchases();
                }
            } else {
                goToExplore();
            }
        }
    }, 1000);
};

onMounted(async () => {
    loading.value = true;
    const params = globalThis.location.search;
    const url = `/api/sales/payment_confirmation${params}`;
    try {
        const response = await axios.get(url);
        sale.value = response.data;
        isEval.value = sale.value.type === "eval_run";
    } catch (error) {
        console.error('Error loading sale data:', error);
    } finally {
        loading.value = false;
        startCountdown();
    }
});

onUnmounted(() => {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
});
</script>

<style scoped>
.min-h-screen {
    min-height: calc(100vh - 64px)!important;
}
</style>
